import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { api, ApiError, getToken, setToken } from './api';
import {
  Appointment, AppointmentStatus,
  AvailabilityDay, DateOverrideItem, EmailTemplateItem, NewBookingPayload,
  ServiceItem, WeeklyDefaultItem,
  plusDaysIso, todayIso,
} from './types';

interface AvailabilityResponse {
  from: string;
  to: string;
  days: AvailabilityDay[];
}

export type PublicPage = 'home' | 'privacy' | 'cookie' | 'terms';

interface AppContextValue {
  // Data
  services: ServiceItem[];
  availability: AvailabilityDay[];
  appointments: Appointment[];
  weeklyDefaults: WeeklyDefaultItem[];
  dateOverrides: DateOverrideItem[];
  emailTemplates: EmailTemplateItem[];

  // Booking
  addAppointment: (payload: NewBookingPayload) => Promise<Appointment>;

  // Admin
  changeAppointmentStatus: (id: number, status: AppointmentStatus) => Promise<void>;
  updateWeeklyDefault: (id: number, patch: Partial<Pick<WeeklyDefaultItem, 'is_open' | 'hours'>>) => Promise<void>;
  upsertDateOverride: (data: Omit<DateOverrideItem, 'id'> & { id?: number }) => Promise<void>;
  deleteDateOverride: (id: number) => Promise<void>;
  updateEmailTemplate: (id: number, patch: Partial<Pick<EmailTemplateItem, 'enabled' | 'subject' | 'body'>>) => Promise<void>;

  // Auth + Dashboard
  dashboardOpen: boolean;
  dashboardUnlocked: boolean;
  loginOpen: boolean;
  openDashboard: () => void;
  closeDashboard: () => void;
  requestDashboard: () => void;
  closeLogin: () => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (oldPwd: string, newPwd: string, newPwdConfirm: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (uid: string, token: string, newPwd: string, newPwdConfirm: string) => Promise<void>;

  // Pagine pubbliche
  page: PublicPage;
  goTo: (p: PublicPage) => void;

  // Booking pre-select
  preselectedService: string | null;
  preselectService: (s: string) => void;
  consumePreselect: () => void;

  // Toast
  toast: string | null;
  showToast: (msg: string) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [weeklyDefaults, setWeeklyDefaults] = useState<WeeklyDefaultItem[]>([]);
  const [dateOverrides, setDateOverrides] = useState<DateOverrideItem[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateItem[]>([]);

  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [dashboardUnlocked, setDashboardUnlocked] = useState<boolean>(!!getToken());
  const [loginOpen, setLoginOpen] = useState(false);
  const [page, setPage] = useState<PublicPage>('home');
  const [preselectedService, setPreselectedService] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 5000);
  }, []);

  // ───── Loaders ─────
  const loadAvailability = useCallback(async () => {
    const from = todayIso();
    const to = plusDaysIso(42);
    try {
      const data = await api<AvailabilityResponse>(`/availability/?from=${from}&to=${to}`);
      setAvailability(data.days);
    } catch (e: any) {
      showToast(`Errore caricamento disponibilità: ${e.message}`);
    }
  }, [showToast]);

  const loadAdminData = useCallback(async () => {
    try {
      const [b, w, o, e] = await Promise.all([
        api<Appointment[]>('/admin/bookings/', { auth: true }),
        api<WeeklyDefaultItem[]>('/admin/weekly-defaults/', { auth: true }),
        api<DateOverrideItem[]>('/admin/date-overrides/', { auth: true }),
        api<EmailTemplateItem[]>('/admin/email-templates/', { auth: true }),
      ]);
      setAppointments(b);
      setWeeklyDefaults(w);
      setDateOverrides(o);
      setEmailTemplates(e);
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 401) {
        setToken(null);
        setDashboardUnlocked(false);
        setDashboardOpen(false);
        showToast('Sessione scaduta. Effettua di nuovo il login.');
      } else {
        showToast(`Errore caricamento dati admin: ${e.message}`);
      }
    }
  }, [showToast]);

  useEffect(() => {
    api<ServiceItem[]>('/services/').then(setServices).catch((e: ApiError) => {
      showToast(`Impossibile caricare i servizi: ${e.message}`);
    });
    loadAvailability();
  }, [showToast, loadAvailability]);

  useEffect(() => {
    if (dashboardUnlocked) loadAdminData();
  }, [dashboardUnlocked, loadAdminData]);

  // ───── Public ─────
  const addAppointment = useCallback(async (payload: NewBookingPayload) => {
    const created = await api<Appointment>('/bookings/', { method: 'POST', body: payload });
    loadAvailability(); // aggiorna gli slot occupati
    if (dashboardUnlocked) loadAdminData();
    return created;
  }, [dashboardUnlocked, loadAvailability, loadAdminData]);

  // ───── Admin: booking status ─────
  const changeAppointmentStatus = useCallback(async (id: number, status: AppointmentStatus) => {
    const prev = appointments;
    setAppointments(prev.map(a => (a.id === id ? { ...a, status } : a)));
    try {
      await api<Appointment>(`/admin/bookings/${id}/set_status/`, {
        method: 'PATCH', body: { status }, auth: true,
      });
      loadAvailability(); // status influenza "occupied"
    } catch (e: any) {
      setAppointments(prev);
      showToast(`Errore aggiornamento stato: ${e.message}`);
    }
  }, [appointments, loadAvailability, showToast]);

  // ───── Admin: weekly default ─────
  const updateWeeklyDefault = useCallback(async (id: number, patch: Partial<Pick<WeeklyDefaultItem, 'is_open' | 'hours'>>) => {
    try {
      const updated = await api<WeeklyDefaultItem>(`/admin/weekly-defaults/${id}/`, {
        method: 'PATCH', body: patch, auth: true,
      });
      setWeeklyDefaults(prev => prev.map(w => (w.id === id ? updated : w)));
      loadAvailability();
    } catch (e: any) {
      showToast(`Errore salvataggio settimana tipo: ${e.message}`);
    }
  }, [loadAvailability, showToast]);

  // ───── Admin: date overrides ─────
  const upsertDateOverride = useCallback(async (data: Omit<DateOverrideItem, 'id'> & { id?: number }) => {
    try {
      if (data.id) {
        const { id, ...rest } = data;
        const updated = await api<DateOverrideItem>(`/admin/date-overrides/${data.date}/`, {
          method: 'PATCH', body: rest, auth: true,
        });
        setDateOverrides(prev => prev.map(o => (o.id === id ? updated : o)));
      } else {
        const created = await api<DateOverrideItem>('/admin/date-overrides/', {
          method: 'POST', body: data, auth: true,
        });
        setDateOverrides(prev => [...prev, created].sort((a, b) => a.date.localeCompare(b.date)));
      }
      loadAvailability();
    } catch (e: any) {
      showToast(`Errore salvataggio eccezione: ${e.message}`);
      throw e;
    }
  }, [loadAvailability, showToast]);

  const deleteDateOverride = useCallback(async (id: number) => {
    const target = dateOverrides.find(o => o.id === id);
    if (!target) return;
    try {
      await api(`/admin/date-overrides/${target.date}/`, { method: 'DELETE', auth: true });
      setDateOverrides(prev => prev.filter(o => o.id !== id));
      loadAvailability();
    } catch (e: any) {
      showToast(`Errore eliminazione eccezione: ${e.message}`);
    }
  }, [dateOverrides, loadAvailability, showToast]);

  const updateEmailTemplate = useCallback(async (id: number, patch: Partial<Pick<EmailTemplateItem, 'enabled' | 'subject' | 'body'>>) => {
    try {
      const updated = await api<EmailTemplateItem>(`/admin/email-templates/${id}/`, {
        method: 'PATCH', body: patch, auth: true,
      });
      setEmailTemplates(prev => prev.map(t => (t.id === id ? updated : t)));
    } catch (e: any) {
      showToast(`Errore salvataggio template email: ${e.message}`);
      throw e;
    }
  }, [showToast]);

  // ───── Dashboard ─────
  const openDashboard = useCallback(() => {
    setDashboardOpen(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const closeDashboard = useCallback(() => {
    setDashboardOpen(false);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const requestDashboard = useCallback(() => {
    if (dashboardOpen) { closeDashboard(); return; }
    if (dashboardUnlocked) openDashboard();
    else setLoginOpen(true);
  }, [dashboardOpen, dashboardUnlocked, openDashboard, closeDashboard]);

  const closeLogin = useCallback(() => setLoginOpen(false), []);

  const login = useCallback(async (username: string, password: string) => {
    const data = await api<{ access: string; refresh: string }>('/auth/login/', {
      method: 'POST',
      body: { username, password },
    });
    setToken(data.access);
    setDashboardUnlocked(true);
    setLoginOpen(false);
    openDashboard();
  }, [openDashboard]);

  const changePassword = useCallback(async (oldPwd: string, newPwd: string, newPwdConfirm: string) => {
    await api('/admin/change-password/', {
      method: 'POST',
      body: { old_password: oldPwd, new_password: newPwd, new_password_confirm: newPwdConfirm },
      auth: true,
    });
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    await api('/auth/password-reset/', { method: 'POST', body: { email } });
  }, []);

  const confirmPasswordReset = useCallback(async (uid: string, token: string, newPwd: string, newPwdConfirm: string) => {
    await api('/auth/password-reset/confirm/', {
      method: 'POST',
      body: { uid, token, new_password: newPwd, new_password_confirm: newPwdConfirm },
    });
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setDashboardUnlocked(false);
    setDashboardOpen(false);
    setAppointments([]);
    setWeeklyDefaults([]);
    setDateOverrides([]);
    setEmailTemplates([]);
  }, []);

  const preselectService = useCallback((s: string) => setPreselectedService(s), []);
  const consumePreselect = useCallback(() => setPreselectedService(null), []);

  const goTo = useCallback((p: PublicPage) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const value: AppContextValue = {
    services, availability, appointments, weeklyDefaults, dateOverrides, emailTemplates,
    addAppointment,
    changeAppointmentStatus, updateWeeklyDefault, upsertDateOverride, deleteDateOverride, updateEmailTemplate,
    dashboardOpen, dashboardUnlocked, loginOpen,
    openDashboard, closeDashboard, requestDashboard, closeLogin, login, logout,
    changePassword, requestPasswordReset, confirmPasswordReset,
    page, goTo,
    preselectedService, preselectService, consumePreselect,
    toast, showToast,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
